import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';

import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';

import { agreementFilters, queueFilters } from './collection.filters';
import { AGREEMENTS_PATH, COLLECTION_PATH, DEBTOR_PATH, QUEUE_PATH, agreementPath, debtorPath } from './collection.paths';
import { scopePatch } from './collection.search';

// Manifestos da Recuperação de crédito (U23, U42). Arquivo leve: entra no `src/app/screens.ts` e no ⌘K.
// Gate próprio `CreditRecovery:Read` (router.tsx:1532-1540); nenhuma ação tem gate no front: a recusa
// de alçada vem do backend (discovery/10, módulo collection).

export const COLLECTION_READ_PERMISSION = can('CreditRecovery', 'Read');

const emptyInput = z.strictObject({});

/** Ação que só existe com a tela montada: a tela troca o `run` pelo dela (padrão do Dashboard). */
const mountedScreenRequired = (): never => {
  throw new Error(t`Abra a tela de Recuperação de crédito para usar esta ação.`);
};

// ─── Painel ──────────────────────────────────────────────────────────────────────────────────────

export const collectionRefresh = action({
  id: 'collection.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo a posição da carteira e as métricas de recuperação, com as carteiras escolhidas.`,
  input: emptyInput,
  run: mountedScreenRequired,
});

export const collectionWallets = action({
  id: 'collection.wallets',
  kind: 'draft',
  label: msg`Selecionar carteiras`,
  description: msg`Abre a gaveta "Gerenciar Carteiras" para a pessoa escolher as carteiras que o painel e a fila consideram. A escolha e a confirmação são da pessoa.`,
  input: emptyInput,
  run: ({ nav }) => nav.search({ sheet: 'carteiras' }),
});

export const collectionToggleValues = action({
  id: 'collection.toggleValues',
  kind: 'read',
  label: msg`Mostrar ou ocultar valores`,
  description: msg`Esconde ou mostra os valores em reais do painel, sem buscar nada de novo.`,
  input: emptyInput,
  run: mountedScreenRequired,
});

export const collectionOpenQueue = action({
  id: 'collection.openQueue',
  kind: 'navigate',
  label: msg`Abrir fila de trabalho`,
  description: msg`Abre a fila de trabalho sobre as mesmas carteiras que o painel mostra.`,
  input: emptyInput,
  run: mountedScreenRequired,
});

export const agingBandInput = z.strictObject({ label: z.string().min(1) });

export const collectionAgingBand = action<z.infer<typeof agingBandInput>>({
  id: 'collection.agingBand',
  kind: 'navigate',
  label: msg`Abrir faixa de atraso na fila`,
  description: msg`Abre a fila dos contratos cujo maior atraso está numa das faixas do gráfico "Vencido por faixa de atraso", pelo rótulo da faixa (ex.: 31-60).`,
  input: agingBandInput,
  run: mountedScreenRequired,
});

export const agingQueueInput = z.object({
  minOverdueDays: z.string().regex(/^\d+$/),
  maxOverdueDays: z.string().regex(/^\d+$/).optional(),
  walletsCode: z.array(z.string().regex(/^\d+$/)).optional(),
});

/**
 * Clique numa faixa do gráfico (CollectionAgingChart.tsx:39-56, 124-127): a fila com o atraso da faixa e as
 * carteiras do painel. Intenção do bloco `collection.aging` resolvida pelo host (U41).
 */
export const collectionOpenAgingQueue = action<z.infer<typeof agingQueueInput>>({
  id: 'collection.openAgingQueue',
  kind: 'navigate',
  label: msg`Abrir fila da faixa`,
  description: msg`Abre a fila de trabalho com o atraso mínimo e máximo de uma faixa de atraso e as carteiras do painel. Nada muda só por abrir.`,
  input: agingQueueInput,
  run: ({ nav, input }) => nav.to(QUEUE_PATH, { search: input }),
});

export const collectionDashboardScreen = defineScreen({
  id: 'collection.dashboard',
  path: COLLECTION_PATH,
  title: msg`Recuperação de crédito`,
  description: msg`Painel da recuperação de crédito: posição da carteira, inadimplência sem e com acordo, vencido por faixa de atraso e as métricas de recuperação, nas carteiras escolhidas.`,
  permission: COLLECTION_READ_PERMISSION,
  place: { icon: 'handshake', section: 'wallet-management' },
  actions: [collectionRefresh, collectionWallets, collectionToggleValues, collectionOpenQueue, collectionAgingBand, collectionOpenAgingQueue],
});

// ─── Fila ────────────────────────────────────────────────────────────────────────────────────────

const effectPreview = () => ({
  title: t`Abrir o formulário na tela`,
  detail: t`Confira os dados e confirme na tela. Nada é gravado sem a confirmação da pessoa.`,
});

export const queueRefresh = action({
  id: 'collection.queueRefresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo a fila de trabalho, com a aba, os filtros e a página atuais.`,
  input: emptyInput,
  run: mountedScreenRequired,
});

export const queuePage = listPageAction('collection.queuePage');
export const queueFilter = filterAction('collection.queueFilter', queueFilters);

export const queueScopeInput = z.strictObject({ scope: z.enum(['mine', 'unassigned', 'all']) });

export const queueScope = action<z.infer<typeof queueScopeInput>>({
  id: 'collection.queueScope',
  kind: 'navigate',
  label: msg`Trocar aba da fila`,
  description: msg`Troca a aba da fila: mine = Minha fila, unassigned = Não atribuídos, all = Todos. Volta à primeira página e limpa a seleção.`,
  input: queueScopeInput,
  run: ({ nav, input }) => nav.search(scopePatch(input.scope)),
});

export const queueSelect = action<{ ids: string[] }>({
  id: 'collection.queueSelect',
  kind: 'draft',
  label: msg`Selecionar parcelamentos`,
  description: msg`Marca parcelamentos da página atual da fila, para atribuir a um operador ou registrar tratativa.`,
  input: z.object({ ids: z.array(z.string()) }),
  run: ({ nav, input }) => nav.search({ selected: input.ids.length > 0 ? input.ids : undefined, sheet: undefined }),
});

export const queueOpen = action<{ id: string }>({
  id: 'collection.queueOpen',
  kind: 'navigate',
  label: msg`Abrir painel do devedor`,
  description: msg`Abre o painel do devedor de uma linha da fila: parcelas, acordos e linha do tempo. Nada muda só por abrir.`,
  input: z.object({ id: z.string() }),
  run: ({ nav, input }) => nav.to(debtorPath(input.id)),
});

export const queueAssign = action({
  id: 'collection.queueAssign',
  kind: 'write',
  label: msg`Atribuir`,
  description: msg`Abre "Atribuir operador" para os parcelamentos marcados na fila. Nada é atribuído sem a confirmação da pessoa.`,
  input: emptyInput,
  preview: effectPreview,
  run: ({ nav }) => nav.search({ sheet: 'atribuir' }),
});

export const queueTreatment = action({
  id: 'collection.queueTreatment',
  kind: 'write',
  label: msg`Registrar tratativa`,
  description: msg`Abre "Registrar tratativa" para o único parcelamento marcado na fila. Nada é registrado sem a confirmação da pessoa.`,
  input: emptyInput,
  preview: effectPreview,
  run: ({ nav }) => nav.search({ sheet: 'tratativa' }),
});

export const collectionQueueScreen = defineScreen({
  id: 'collection.queue',
  path: QUEUE_PATH,
  title: msg`Fila de trabalho`,
  description: msg`Fila de trabalho da recuperação: um parcelamento vencido por linha, com abas Minha fila, Não atribuídos e Todos, filtros, ordenação, seleção para atribuir operador e registro de tratativa.`,
  permission: COLLECTION_READ_PERMISSION,
  place: { icon: 'file-text', parent: 'collection.dashboard' },
  actions: [queueRefresh, queuePage, queueFilter, queueScope, queueSelect, queueOpen, queueAssign, queueTreatment],
});

// ─── Painel do devedor ───────────────────────────────────────────────────────────────────────────

export const debtorBack = action({
  id: 'collection.debtorBack',
  kind: 'navigate',
  label: msg`Voltar`,
  description: msg`Volta à tela de onde a pessoa veio; aberto direto pelo link, volta à fila de trabalho.`,
  input: emptyInput,
  run: mountedScreenRequired,
});

export const debtorRefresh = action({
  id: 'collection.debtorRefresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo o devedor aberto: cabeçalho, parcelas, acordos e linha do tempo.`,
  input: emptyInput,
  run: mountedScreenRequired,
});

export const debtorTreatment = action({
  id: 'collection.debtorTreatment',
  kind: 'write',
  label: msg`Registrar tratativa`,
  description: msg`Abre "Registrar tratativa" do contrato aberto na tela. Nada é registrado sem a confirmação da pessoa.`,
  input: emptyInput,
  preview: effectPreview,
  run: ({ nav }) => nav.search({ sheet: 'tratativa' }),
});

export const debtorSimulate = action({
  id: 'collection.debtorSimulate',
  kind: 'draft',
  label: msg`Simular`,
  description: msg`Abre "Simular valores" sobre as parcelas marcadas do contrato aberto (ou sobre todo o vencido, sem marcação). Simular não grava nada.`,
  input: emptyInput,
  run: mountedScreenRequired,
});

export const debtorAgreement = action({
  id: 'collection.debtorAgreement',
  kind: 'write',
  label: msg`Montar acordo`,
  description: msg`Abre "Montar acordo" do contrato aberto, com as parcelas marcadas. O acordo nasce como rascunho e só é criado com a confirmação da pessoa.`,
  input: emptyInput,
  preview: effectPreview,
  run: ({ nav }) => nav.search({ sheet: 'acordo' }),
});

export const debtorSelect = action<{ ids: string[] }>({
  id: 'collection.debtorSelect',
  kind: 'draft',
  label: msg`Selecionar parcelas`,
  description: msg`Marca as parcelas do contrato que o acordo vai renegociar. Parcela paga, baixada, renegociada ou de antecipação não pode ser marcada.`,
  input: z.object({ ids: z.array(z.string()) }),
  run: mountedScreenRequired,
});

export const debtorOnlyOverdue = action<{ only: boolean }>({
  id: 'collection.debtorOnlyOverdue',
  kind: 'read',
  label: msg`Somente vencidas`,
  description: msg`Mostra só as parcelas vencidas na lista do contrato, ou todas de novo. Não muda a situação do devedor no cabeçalho.`,
  input: z.object({ only: z.boolean() }),
  run: mountedScreenRequired,
});

export const debtorOpenAgreement = action<{ id: string }>({
  id: 'collection.debtorOpenAgreement',
  kind: 'navigate',
  label: msg`Abrir acordo`,
  description: msg`Abre um acordo deste contrato (da lista de acordos ou do selo da parcela): termos, parcelas, histórico e as ações do estado em que ele está.`,
  input: z.object({ id: z.string() }),
  run: mountedScreenRequired,
});

export const collectionDebtorScreen = defineScreen({
  id: 'collection.debtor',
  path: `${DEBTOR_PATH}/$installmentId`,
  title: msg`Painel do devedor`,
  description: msg`Painel do devedor de um contrato: situação do devedor, parcelas com seleção para o acordo, acordos do contrato e linha do tempo, com tratativa, simulação e montagem de acordo em rascunho.`,
  permission: COLLECTION_READ_PERMISSION,
  place: { icon: 'user', parent: 'collection.dashboard', record: msg`Devedor` },
  actions: [debtorBack, debtorRefresh, debtorTreatment, debtorSimulate, debtorAgreement, debtorSelect, debtorOnlyOverdue, debtorOpenAgreement],
});

// ─── Acordos ─────────────────────────────────────────────────────────────────────────────────────

export const agreementsRefresh = action({
  id: 'collection.agreementsRefresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo a lista de acordos, com os filtros e a página atuais.`,
  input: emptyInput,
  run: mountedScreenRequired,
});

export const agreementsPage = listPageAction('collection.agreementsPage');
export const agreementsFilter = filterAction('collection.agreementsFilter', agreementFilters);

export const agreementsOpen = action<{ id: string }>({
  id: 'collection.agreementsOpen',
  kind: 'navigate',
  label: msg`Abrir acordo`,
  description: msg`Abre um acordo da lista: termos, parcelas, histórico e as ações do estado em que ele está. Nada muda só por abrir.`,
  input: z.object({ id: z.string() }),
  run: ({ nav, input }) => nav.to(agreementPath(input.id)),
});

export const collectionAgreementsScreen = defineScreen({
  id: 'collection.agreements',
  path: AGREEMENTS_PATH,
  title: msg`Acordos`,
  description: msg`Lista dos acordos de recuperação de crédito, com data, devedor, valor, vencimento, situação, operador e canal, e filtros por situação, operador e data do acordo. O acordo nasce no painel do devedor, não aqui.`,
  permission: COLLECTION_READ_PERMISSION,
  place: { icon: 'handshake', section: 'wallet-management' },
  actions: [agreementsRefresh, agreementsPage, agreementsFilter, agreementsOpen],
});

export const agreementRefresh = action({
  id: 'collection.agreementRefresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo o acordo aberto: termos, classificação, cadeia de acordos e histórico de transições.`,
  input: emptyInput,
  run: mountedScreenRequired,
});

export const agreementNegotiate = action({
  id: 'collection.agreementNegotiate',
  kind: 'write',
  label: msg`Fechar termos`,
  description: msg`Abre "Fechar termos do acordo" do acordo aberto, em Rascunho, para confirmar os valores pactuados com o devedor; o acordo passa a Negociado. Nada é gravado sem a confirmação da pessoa.`,
  input: emptyInput,
  preview: effectPreview,
  run: ({ nav }) => nav.search({ sheet: 'fechar-termos' }),
});

export const agreementFormalize = action({
  id: 'collection.agreementFormalize',
  kind: 'write',
  label: msg`Formalizar`,
  description: msg`Abre "Formalizar acordo" do acordo aberto, em Negociado. Formalizar emite os boletos de verdade e não tem volta. Nada é emitido sem a confirmação da pessoa.`,
  input: emptyInput,
  preview: effectPreview,
  run: ({ nav }) => nav.search({ sheet: 'formalizar' }),
});

export const agreementRenegotiate = action({
  id: 'collection.agreementRenegotiate',
  kind: 'write',
  label: msg`Renegociar`,
  description: msg`Abre "Renegociar acordo" do acordo aberto (Formalizado, Em andamento ou Quebrado): cria um acordo novo, em Rascunho, que sucede este, e este passa a Substituído. Nada é gravado sem a confirmação da pessoa.`,
  input: emptyInput,
  preview: effectPreview,
  run: ({ nav }) => nav.search({ sheet: 'renegociar' }),
});

export const agreementCancel = action({
  id: 'collection.agreementCancel',
  kind: 'destructive',
  label: msg`Cancelar acordo`,
  description: msg`Abre "Cancelar acordo" do acordo aberto (Rascunho, Negociado ou Formalizado), que pede a situação detalhada e o motivo. Nada é cancelado sem a confirmação da pessoa.`,
  input: emptyInput,
  preview: () => ({ ...effectPreview(), tone: 'destructive' as const }),
  run: ({ nav }) => nav.search({ sheet: 'cancelar' }),
});

export const agreementViewDebtor = action({
  id: 'collection.agreementViewDebtor',
  kind: 'navigate',
  label: msg`Ver devedor`,
  description: msg`Abre o painel do devedor do contrato deste acordo.`,
  input: emptyInput,
  run: mountedScreenRequired,
});

export const agreementViewGenerated = action({
  id: 'collection.agreementViewGenerated',
  kind: 'navigate',
  label: msg`Ver parcelamento gerado`,
  description: msg`Depois de formalizado, abre o parcelamento onde estão os boletos do acordo: o parcelamento novo ou, em parcela única, o contrato de origem com o boleto de antecipação.`,
  input: emptyInput,
  run: mountedScreenRequired,
});

export const agreementOpenChain = action<{ id: string }>({
  id: 'collection.agreementOpenChain',
  kind: 'navigate',
  label: msg`Abrir acordo da cadeia`,
  description: msg`Abre outro acordo do mesmo devedor, da lista "Acordos deste devedor". Nada muda só por abrir.`,
  input: z.object({ id: z.string() }),
  run: mountedScreenRequired,
});

export const collectionAgreementScreen = defineScreen({
  id: 'collection.agreement',
  path: `${AGREEMENTS_PATH}/$agreementId`,
  title: msg`Acordo`,
  description: msg`Detalhe de um acordo de recuperação: identificação, termos, parcelas que a formalização emite, classificação, observações, a cadeia de acordos do devedor, o rastro e o histórico de transições, com as ações que o estado do acordo permite.`,
  permission: COLLECTION_READ_PERMISSION,
  place: { icon: 'file-text', parent: 'collection.agreements', record: msg`Acordo` },
  actions: [
    agreementRefresh,
    agreementNegotiate,
    agreementFormalize,
    agreementRenegotiate,
    agreementCancel,
    agreementViewDebtor,
    agreementViewGenerated,
    agreementOpenChain,
  ],
});

// ─── Contexto para o agente (U24, U46 D2-C): o que a pessoa vê, sem nome nem documento ─────────────

export function dashboardContext(input: {
  readonly wallets: readonly number[];
  readonly serverScoped: boolean;
  readonly loading: boolean;
  readonly overdueTotal: number | undefined;
  readonly hidden: boolean;
}): ScreenContext {
  const count = input.wallets.length;
  const scope = count === 0 ? t`Todas as carteiras visíveis.` : t`Carteiras no recorte: ${count}.`;
  const server = input.serverScoped ? t`O recorte foi escolhido pelo servidor.` : '';
  const numbers = input.loading
    ? t`Carregando.`
    : input.overdueTotal === undefined
      ? t`Posição indisponível.`
      : t`Contratos com vencido: ${input.overdueTotal}.`;
  return {
    summary: [t`Painel de recuperação de crédito.`, scope, server, numbers, input.hidden ? t`Valores ocultos.` : ''].filter(Boolean).join(' '),
    fields: { wallets: input.wallets.length, serverScoped: input.serverScoped, overdueContracts: input.overdueTotal ?? null, valuesHidden: input.hidden },
    sensitivity: 'internal',
  };
}

export function queueContext(input: {
  readonly scope: string;
  readonly page: number;
  readonly total: number | undefined;
  readonly filters: number;
  readonly selected: number;
}): ScreenContext {
  const { scope, page, total, filters, selected } = input;
  return {
    summary: [
      t`Fila de trabalho, aba ${scope}, página ${page}.`,
      total === undefined ? t`Carregando.` : t`Parcelamentos encontrados: ${total}.`,
      filters > 0 ? t`Filtros aplicados: ${filters}.` : t`Sem filtros.`,
      selected > 0 ? t`Selecionados: ${selected}.` : '',
    ]
      .filter(Boolean)
      .join(' '),
    fields: { scope, page, total: total ?? null, filters, selectedCount: selected },
    sensitivity: 'internal',
  };
}

export function debtorContext(input: {
  readonly installmentId: string;
  readonly loaded: boolean;
  readonly billings: number | undefined;
  readonly selected: number;
  readonly agreements: number | undefined;
  readonly simulated: boolean;
}): ScreenContext {
  if (!input.loaded) return { summary: t`Painel do devedor carregando.`, fields: { installmentId: input.installmentId }, sensitivity: 'internal' };
  const billings = input.billings ?? 0;
  const agreements = input.agreements ?? 0;
  return {
    summary: [
      t`Painel do devedor aberto.`,
      t`Parcelas no contrato: ${billings}.`,
      input.selected > 0 ? t`Parcelas marcadas: ${input.selected}.` : t`Nenhuma parcela marcada.`,
      t`Acordos do contrato: ${agreements}.`,
      input.simulated ? t`Há uma simulação feita para as parcelas marcadas.` : '',
    ]
      .filter(Boolean)
      .join(' '),
    entity: { type: 'Installment', id: input.installmentId },
    fields: { billings: input.billings ?? null, selectedCount: input.selected, agreements: input.agreements ?? null, simulated: input.simulated },
    sensitivity: 'internal',
  };
}

export function agreementsContext(input: { readonly page: number; readonly total: number | undefined; readonly filters: number; readonly fromUrl: number }): ScreenContext {
  const { page, total } = input;
  const filters = input.filters + input.fromUrl;
  return {
    summary: [
      t`Lista de acordos, página ${page}.`,
      total === undefined ? t`Carregando.` : t`Acordos encontrados: ${total}.`,
      filters > 0 ? t`Filtros aplicados: ${filters}.` : t`Sem filtros.`,
    ].join(' '),
    fields: { page, total: total ?? null, filters },
    sensitivity: 'internal',
  };
}

export function agreementContext(input: {
  readonly agreementId: string;
  readonly loaded: boolean;
  readonly status: string | undefined;
  readonly statusLabel: string | undefined;
  readonly installments: number | undefined;
  readonly chain: number | undefined;
  readonly sheet: string | undefined;
}): ScreenContext {
  if (!input.loaded) return { summary: t`Acordo carregando.`, fields: { agreementId: input.agreementId }, sensitivity: 'internal' };
  const label = input.statusLabel ?? '';
  const installments = input.installments ?? 0;
  const chain = input.chain ?? 0;
  return {
    summary: [t`Acordo aberto, em ${label}.`, t`Parcelas pactuadas: ${installments}.`, t`Acordos na cadeia do devedor: ${chain}.`, input.sheet ? t`Há um formulário aberto na tela.` : ''].filter(Boolean).join(' '),
    entity: { type: 'Agreement', id: input.agreementId },
    fields: { status: input.status ?? null, installmentsCount: input.installments ?? null, chain: input.chain ?? null, formOpen: input.sheet !== undefined },
    sensitivity: 'internal',
  };
}
